import ErrorAlert from "../../design-system/primitives/components/ErrorAlert/ErrorAlert";
import Select from "../../design-system/primitives/components/Select/Select";
import Text from "../../design-system/primitives/components/Text/Text";
import Textarea from "../../design-system/primitives/components/Textarea/Textarea";
import TextInput from "../../design-system/primitives/components/TextInput/TextInput";
import { rules } from "../../lib/rules.jsx";
import type { FeedbackFormField } from "../../api/feedback";
import styles from "./feedbackFormFields.css";

/**
 * Every answer is held as a string while the form is open — that keeps the
 * length rules below applicable to `number` fields too. The host converts
 * number fields back on submit.
 */
export type FeedbackFieldValues = Record<string, string>;

/**
 * A rule as `lib/rules.jsx` hands them out: a `[predicate, message]` pair in a
 * plain array, since that module is untyped JS. The three rules a feedback form
 * can carry all use a fixed message; {@link breachedMessage} checks the shape
 * rather than asserting it.
 */
type LooseRule = (string | ((value: string) => boolean))[];

const toRules = (field: FeedbackFormField): LooseRule[] => {
  const list: LooseRule[] = [];
  for (const rule of field.rules ?? []) {
    if (rule.type === "required") list.push(rules.isRequired);
    if (rule.type === "minLength" && rule.value !== undefined) {
      list.push(rules.minLength(rule.value));
    }
    if (rule.type === "maxLength" && rule.value !== undefined) {
      list.push(rules.maxLength(rule.value));
    }
  }
  return list;
};

const breachedMessage = (rule: LooseRule, value: string): string | null => {
  const [isValid, message] = rule;
  if (typeof isValid !== "function" || isValid(value)) return null;
  return typeof message === "string" ? message : "Ungültige Eingabe";
};

const isFieldRequired = (field: FeedbackFormField): boolean =>
  (field.rules ?? []).some((rule) => rule.type === "required");

/** The first rule this value breaks, or `null` when it satisfies all of them. */
export const validateField = (
  field: FeedbackFormField,
  value: string,
): string | null => {
  // An empty optional field is simply unanswered — the toast drops it from the
  // answers rather than sending "" — so the length rules only apply once
  // something has been typed. Without this a `minLength` on a field nobody has
  // to fill in would disable "Senden" for good.
  if (value === "" && !isFieldRequired(field)) return null;
  for (const rule of toRules(field)) {
    const message = breachedMessage(rule, value);
    if (message) return message;
  }
  return null;
};

export const isFormValid = (
  fields: FeedbackFormField[],
  values: FeedbackFieldValues,
): boolean =>
  fields.every(
    (field) => validateField(field, values[field.key] ?? "") === null,
  );

// Sanity validates the field types of a feedback form, so an unsupported one
// means the two ends have drifted apart. Warn once per type rather than on
// every keystroke, and render nothing.
const warnedTypes = new Set<string>();
const warnUnsupported = (type: string) => {
  if (warnedTypes.has(type)) return;
  warnedTypes.add(type);
  console.warn(`FeedbackFormFields: unsupported field type "${type}"`);
};

type Props = {
  fields: FeedbackFormField[];
  values: FeedbackFieldValues;
  errors: Record<string, string>;
  onChange: (key: string, value: string) => void;
};

/**
 * The compact renderer for a feedback form, sized to live inside a toast: no
 * per-question headings and no wizard, just the inputs stacked under their own
 * labels. The bigger `ShowroomForm` renderer stays as it is.
 */
export const FeedbackFormFields = ({
  fields,
  values,
  errors,
  onChange,
}: Props) => (
  <div className={styles.fields}>
    {fields.map((field) => {
      const value = values[field.key] ?? "";
      const error = errors[field.key];
      const required = isFieldRequired(field);

      // The inputs put their own `id` on `field.key`, so these two stay clear
      // of it — an id collision would point `aria-describedby` at the input.
      const descriptionId = field.description
        ? `feedback-${field.key}-description`
        : undefined;
      const errorId = error ? `feedback-${field.key}-error` : undefined;
      // Both belong to the field, and the attribute takes a space-separated
      // list, read out in the order given.
      const describedBy =
        [descriptionId, errorId].filter(Boolean).join(" ") || undefined;

      // The primitives own the label, and there is no slot between it and the
      // control, so the hint sits below the input rather than above it.
      const footer = (
        <>
          {field.description ? (
            <Text id={descriptionId} variant="body-small" color="neutral-100">
              {field.description}
            </Text>
          ) : null}
          {error ? <ErrorAlert id={errorId} title={error} /> : null}
        </>
      );

      if (field.type === "singleLine" || field.type === "number") {
        return (
          <div key={field.key} className={styles.field}>
            <TextInput
              name={field.key}
              label={field.label}
              value={value}
              // Native validation only fires on a `<form>` submit, and there is
              // no form here — the toast submits from its own actions row — so
              // this cannot preempt our messages with a browser bubble. It is
              // what draws the asterisk on the label.
              required={required}
              // A `number` field is held and validated as a string so the
              // length rules keep applying; asking for the numeric keypad is
              // all it wants from the input itself.
              inputMode={field.type === "number" ? "numeric" : undefined}
              error={Boolean(error)}
              aria-describedby={describedBy}
              onChange={(e) => onChange(field.key, e.currentTarget.value)}
            />
            {footer}
          </div>
        );
      }

      if (field.type === "multiLine") {
        return (
          <div key={field.key} className={styles.field}>
            <Textarea
              name={field.key}
              label={field.label}
              value={value}
              required={required}
              error={Boolean(error)}
              aria-describedby={describedBy}
              onChange={(e) => onChange(field.key, e.currentTarget.value)}
            />
            {footer}
          </div>
        );
      }

      if (field.type === "selection") {
        return (
          <div key={field.key} className={styles.field}>
            <Select
              name={field.key}
              label={field.label}
              // Radix reserves the empty string — it may not be an item's
              // value, and passing it as the selection would leave the trigger
              // blank instead of showing the placeholder.
              value={value || undefined}
              placeholder="Bitte auswählen"
              options={(field.options ?? []).map((option) => ({
                value: option,
                label: option,
              }))}
              required={required}
              error={Boolean(error)}
              aria-describedby={describedBy}
              onValueChange={(next) => onChange(field.key, next)}
            />
            {footer}
          </div>
        );
      }

      warnUnsupported(field.type);
      return null;
    })}
  </div>
);
